<template>
  <form @submit.prevent="handleSubmit">
    <div class="form-field">
      <input-text
        elem-id="soItemAttributeName"
        data-testid="so-item-attributes-name-input"
        v-model="formData.so_item_attribute_name"
        :label="t('Nome do Atributo de Item')"
        :placeholder="t('Digite o nome do atributo de item')"
        type="text"
        required
        :validation="validateName"
        fluid
      />
      <small v-if="errors.so_item_attribute_name" class="error-message">{{ errors.so_item_attribute_name }}</small>
    </div>

    <div class="form-field">
      <SelectList
        elem-id="soItemAttributeType"
        v-model="formData.so_item_attribute_type"
        :itens-select="attributeTypeOptions"
        :label="t('Tipo')"
        :placeholder="t('Selecione o tipo')"
        ng-required="true"
        :validation="validateType"
        :auto-select-first="false"
      />
      <small v-if="errors.so_item_attribute_type" class="error-message">{{ errors.so_item_attribute_type }}</small>
    </div>

    <div v-if="isEditMode && formData.so_item_attribute_type === 'select'" class="options-section">
      <h3 class="section-title">{{ t('Opções do Atributo') }}</h3>

      <div v-if="attributeOptions.length === 0" class="empty-state">
        {{ t('Nenhuma opção cadastrada') }}
      </div>

      <div v-else class="options-list">
        <div
          v-for="option in attributeOptions"
          :key="option.so_item_attribute_option_id"
          class="option-row"
        >
          <div class="option-info">
            <div class="option-main">
              <span v-if="option.so_item_attribute_option_icon_svg" class="option-icon" v-html="sanitizeSvgIcon(option.so_item_attribute_option_icon_svg)"></span>
              <span class="option-value">{{ option.so_item_attribute_option_value }}</span>
            </div>
            <span class="option-order">{{ t('Ordem') }}: {{ option.so_item_attribute_option_display_order ?? 0 }}</span>
          </div>
          <div class="option-actions">
            <Button
              icon="pi pi-pencil"
              severity="info"
              size="small"
              text
              type="button"
              data-testid="so-item-attributes-edit-option-btn"
              @click="handleEditOption(option)"
            />
            <Button
              icon="pi pi-trash"
              severity="danger"
              size="small"
              text
              type="button"
              data-testid="so-item-attributes-delete-option-btn"
              @click="handleDeleteOption(option.so_item_attribute_option_id)"
            />
          </div>
        </div>
      </div>

      <div class="options-form">
        <div class="flex-container">
          <div class="flex-item-half">
            <div class="form-field">
              <input-text
                elem-id="soItemAttributeOptionValue"
                data-testid="so-item-attributes-option-value-input"
                v-model="newOption.so_item_attribute_option_value"
                :label="t('Valor da Opção')"
                :placeholder="t('Digite o valor da opção')"
                type="text"
                required
                :validation="validateOptionValue"
              />
              <small v-if="optionErrors.so_item_attribute_option_value" class="error-message">{{ optionErrors.so_item_attribute_option_value }}</small>
            </div>
          </div>

          <div class="flex-item-half">
            <div class="form-field">
              <input-number
                elem-id="soItemAttributeOptionDisplayOrder"
                data-testid="so-item-attributes-option-display-order-input"
                v-model="newOption.so_item_attribute_option_display_order"
                :label="t('Ordem de Exibição')"
                :placeholder="t('Digite a ordem')"
                :min="0"
                :maxFractionDigits="0"
                :validation="validateOptionDisplayOrder"
              />
              <small v-if="optionErrors.so_item_attribute_option_display_order" class="error-message">{{ optionErrors.so_item_attribute_option_display_order }}</small>
            </div>
          </div>
        </div>

        <div class="form-field">
          <input-text
            elem-id="soItemAttributeOptionIconSvg"
            data-testid="so-item-attributes-option-icon-svg-input"
            v-model="newOption.so_item_attribute_option_icon_svg"
            :label="t('Ícone SVG (opcional)')"
            :placeholder="t('Cole o conteúdo SVG aqui')"
          />
          <div v-if="newOption.so_item_attribute_option_icon_svg" class="svg-preview">
            <span class="svg-preview-label">{{ t('Preview') }}:</span>
            <span class="svg-preview-icon" v-html="sanitizeSvgIcon(newOption.so_item_attribute_option_icon_svg)"></span>
          </div>
        </div>

        <div class="options-actions">
          <Button
            v-if="editingOptionId"
            :label="t('Cancelar')"
            icon="pi pi-times"
            size="small"
            severity="secondary"
            text
            type="button"
            data-testid="so-item-attributes-cancel-edit-option-btn"
            @click="cancelEditOption"
          />
          <Button
            :label="isSavingOption ? t('Salvando') : editingOptionId ? t('Salvar Opção') : t('Adicionar Opção')"
            :icon="editingOptionId ? 'pi pi-check' : 'pi pi-plus'"
            size="small"
            :loading="isSavingOption"
            type="button"
            data-testid="so-item-attributes-add-option-btn"
            @click="handleAddOption"
          />
        </div>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { sanitizeSvgIcon } from '../../utils/sanitizeSvgIcon';
import SelectList from '../../inputs/SelectList.vue';
import { useSOItemAttributes } from '../../composables/useSOItemAttributes';
import { eventBus } from '../../eventBus';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface SelectOption {
  ID: Id;
  Name: string;
}

interface ItemAttributeRow {
  so_item_attribute_id?: Id;
  so_item_attribute_name?: string;
  so_item_attribute_type?: string;
}

interface ItemAttributeOption {
  so_item_attribute_option_id?: Id;
  so_item_attribute_option_value?: string;
  so_item_attribute_option_display_order?: number | null;
  so_item_attribute_option_icon_svg?: string | null;
  so_item_attribute_option_attribute_id?: Id;
}

interface Props {
  itemId?: Id | null;
  isEditMode?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  itemId: null,
  isEditMode: false,
});

const emit = defineEmits<{
  'submit-success': [];
  'submit-error': [error: unknown];
}>();

const { t } = useI18n();

const { fetchAll, save, fetchOptions, saveOption, removeOption } = useSOItemAttributes();

const itemIdNumber = computed<number | null>(() => {
  if (!props.itemId) return null;
  const num = typeof props.itemId === 'string' ? parseInt(props.itemId, 10) : props.itemId;
  log('SOItemAttributesForm', 'itemIdNumber', 'Converted ID', { original: props.itemId, converted: num });
  return num;
});

const attributeTypeOptions = ref<SelectOption[]>([
  { ID: 'text', Name: t('text') },
  { ID: 'textarea', Name: t('Texto longo') },
  { ID: 'number', Name: t('number') },
  { ID: 'select', Name: t('select') },
  { ID: 'boolean', Name: t('boolean') },
  { ID: 'checkbox', Name: t('Checkbox (Alertas)') },
  { ID: 'photo', Name: t('Foto') }
]);

const formData = reactive<{
  so_item_attribute_name: string;
  so_item_attribute_type: string;
}>({
  so_item_attribute_name: '',
  so_item_attribute_type: ''
});

const errors = reactive<{
  so_item_attribute_name: string;
  so_item_attribute_type: string;
}>({
  so_item_attribute_name: '',
  so_item_attribute_type: ''
});

const newOption = reactive<{
  so_item_attribute_option_value: string;
  so_item_attribute_option_display_order: number | null;
  so_item_attribute_option_icon_svg: string;
}>({
  so_item_attribute_option_value: '',
  so_item_attribute_option_display_order: 0,
  so_item_attribute_option_icon_svg: ''
});

const optionErrors = reactive<{
  so_item_attribute_option_value: string;
  so_item_attribute_option_display_order: string;
}>({
  so_item_attribute_option_value: '',
  so_item_attribute_option_display_order: ''
});

const isLoading = ref<boolean>(false);
const isLoadingOptions = ref<boolean>(false);
const isSavingOption = ref<boolean>(false);
const attributeOptions = ref<ItemAttributeOption[]>([]);
const editingOptionId = ref<Id | null>(null);

const validateName = (): boolean => {
  return !!formData.so_item_attribute_name && formData.so_item_attribute_name.trim() !== '' && formData.so_item_attribute_name.length <= 255;
};

const validateType = (): boolean => {
  return !!formData.so_item_attribute_type && formData.so_item_attribute_type.trim() !== '';
};

const validateOptionValue = (): boolean => {
  return !!newOption.so_item_attribute_option_value && newOption.so_item_attribute_option_value.trim() !== '';
};

const validateOptionDisplayOrder = (): boolean => {
  return newOption.so_item_attribute_option_display_order == null || newOption.so_item_attribute_option_display_order >= 0;
};

const loadOptions = async (): Promise<void> => {
  if (!itemIdNumber.value || formData.so_item_attribute_type !== 'select') {
    attributeOptions.value = [];
    return;
  }

  log('SOItemAttributesForm', 'loadOptions', 'Loading item attribute options', itemIdNumber.value);
  isLoadingOptions.value = true;

  try {
    attributeOptions.value = await fetchOptions(itemIdNumber.value) as ItemAttributeOption[];
    log('SOItemAttributesForm', 'loadOptions', 'Loaded item attribute options', attributeOptions.value);
  } catch (error) {
    logError('SOItemAttributesForm', 'loadOptions', 'Error loading item attribute options', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao carregar opções do atributo de item') + ': ' + message);
  } finally {
    isLoadingOptions.value = false;
  }
};

const loadForEdit = async (): Promise<void> => {
  if (!itemIdNumber.value) {
    logWarn('SOItemAttributesForm', 'loadForEdit', 'No itemId provided');
    return;
  }

  log('SOItemAttributesForm', 'loadForEdit', 'Loading item attribute for edit', itemIdNumber.value);
  isLoading.value = true;

  try {
    const items = await fetchAll() as ItemAttributeRow[];
    const item = items.find(entry => Number(entry.so_item_attribute_id) === Number(itemIdNumber.value));
    log('SOItemAttributesForm', 'loadForEdit', 'Loaded data', item);

    if (!item) {
      throw new Error('Dados do atributo de item nao encontrados');
    }

    formData.so_item_attribute_name = item.so_item_attribute_name || '';
    formData.so_item_attribute_type = item.so_item_attribute_type || '';

    clearErrors();
    await loadOptions();
  } catch (error) {
    const err = error as { message?: string; stack?: string; response?: { data?: { error?: string } } };
    logError('SOItemAttributesForm', 'loadForEdit', 'Error loading item attribute', {
      itemId: itemIdNumber.value,
      error: err.message,
      stack: err.stack,
      response: err.response
    });

    const errorMessage = err.response?.data?.error || err.message || 'Erro desconhecido';
    eventBus.emit('globalMessage.error', t('Erro ao carregar atributo de item') + ': ' + errorMessage);
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

const validateForm = (): boolean => {
  log('SOItemAttributesForm', 'validateForm', 'Validating form');
  clearErrors();
  let isValid = true;

  if (!formData.so_item_attribute_name || formData.so_item_attribute_name.trim() === '') {
    errors.so_item_attribute_name = t('Campo obrigatório');
    isValid = false;
  } else if (formData.so_item_attribute_name.length > 255) {
    errors.so_item_attribute_name = t('Nome muito longo') + ' (max 255)';
    isValid = false;
  }

  if (!formData.so_item_attribute_type || formData.so_item_attribute_type.trim() === '') {
    errors.so_item_attribute_type = t('Campo obrigatório');
    isValid = false;
  }

  log('SOItemAttributesForm', 'validateForm', 'Validation result', isValid);
  return isValid;
};

const validateOptionForm = (): boolean => {
  log('SOItemAttributesForm', 'validateOptionForm', 'Validating option form');
  clearOptionErrors();
  let isValid = true;

  if (!newOption.so_item_attribute_option_value || newOption.so_item_attribute_option_value.trim() === '') {
    optionErrors.so_item_attribute_option_value = t('Campo obrigatório');
    isValid = false;
  }

  if (newOption.so_item_attribute_option_display_order != null && newOption.so_item_attribute_option_display_order < 0) {
    optionErrors.so_item_attribute_option_display_order = t('Campo inválido');
    isValid = false;
  }

  log('SOItemAttributesForm', 'validateOptionForm', 'Validation result', isValid);
  return isValid;
};

const clearErrors = (): void => {
  errors.so_item_attribute_name = '';
  errors.so_item_attribute_type = '';
};

const clearOptionErrors = (): void => {
  optionErrors.so_item_attribute_option_value = '';
  optionErrors.so_item_attribute_option_display_order = '';
};

const resetOptionForm = (): void => {
  log('SOItemAttributesForm', 'resetOptionForm', 'Resetting option form');
  editingOptionId.value = null;
  newOption.so_item_attribute_option_value = '';
  newOption.so_item_attribute_option_display_order = 0;
  newOption.so_item_attribute_option_icon_svg = '';
  clearOptionErrors();
};

const handleEditOption = (option: ItemAttributeOption): void => {
  editingOptionId.value = option.so_item_attribute_option_id ?? null;
  newOption.so_item_attribute_option_value = option.so_item_attribute_option_value || '';
  newOption.so_item_attribute_option_display_order = option.so_item_attribute_option_display_order ?? 0;
  newOption.so_item_attribute_option_icon_svg = option.so_item_attribute_option_icon_svg || '';
  clearOptionErrors();
};

const cancelEditOption = (): void => {
  resetOptionForm();
};

const resetForm = (): void => {
  log('SOItemAttributesForm', 'resetForm', 'Resetting form');
  formData.so_item_attribute_name = '';
  formData.so_item_attribute_type = '';
  attributeOptions.value = [];
  clearErrors();
  resetOptionForm();
};

const getItemName = (): string => formData.so_item_attribute_name;

const handleAddOption = async (): Promise<void> => {
  log('SOItemAttributesForm', 'handleAddOption', 'Adding new item attribute option');

  if (!itemIdNumber.value) {
    logWarn('SOItemAttributesForm', 'handleAddOption', 'Cannot add option without item ID');
    return;
  }

  if (!validateOptionForm()) {
    logWarn('SOItemAttributesForm', 'handleAddOption', 'Option validation failed');
    return;
  }

  isSavingOption.value = true;

  try {
    const baseOption = {
      so_item_attribute_option_value: newOption.so_item_attribute_option_value.trim(),
      so_item_attribute_option_display_order: newOption.so_item_attribute_option_display_order ?? 0,
      so_item_attribute_option_icon_svg: newOption.so_item_attribute_option_icon_svg || undefined
    };

    const optionPayload = editingOptionId.value
      ? { ...baseOption, so_item_attribute_option_id: Number(editingOptionId.value) }
      : { ...baseOption, so_item_attribute_option_attribute_id: itemIdNumber.value ?? undefined };

    await saveOption(optionPayload);

    eventBus.emit('globalMessage.success',
      editingOptionId.value ? t('Opção do atributo atualizada com sucesso') : t('Opção do atributo criada com sucesso')
    );
    resetOptionForm();
    await loadOptions();
  } catch (error) {
    logError('SOItemAttributesForm', 'handleAddOption', 'Error saving item attribute option', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao salvar opção do atributo de item') + ': ' + message);
  } finally {
    isSavingOption.value = false;
  }
};

const handleDeleteOption = async (optionId: Id | undefined): Promise<void> => {
  log('SOItemAttributesForm', 'handleDeleteOption', 'Deleting item attribute option', optionId);

  if (optionId === undefined) return;

  try {
    await removeOption(Number(optionId));
    eventBus.emit('globalMessage.success', t('Opção do atributo deletada com sucesso'));
    await loadOptions();
  } catch (error) {
    logError('SOItemAttributesForm', 'handleDeleteOption', 'Error deleting item attribute option', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao deletar opção do atributo de item') + ': ' + message);
  }
};

const handleSubmit = async (): Promise<void> => {
  log('SOItemAttributesForm', 'handleSubmit', 'Form submitted');

  if (!validateForm()) {
    logWarn('SOItemAttributesForm', 'handleSubmit', 'Validation failed');
    return;
  }

  isLoading.value = true;

  try {
    if (props.isEditMode && itemIdNumber.value) {
      const payload = {
        so_item_attribute_name: formData.so_item_attribute_name.trim(),
        so_item_attribute_type: formData.so_item_attribute_type,
        so_item_attribute_id: itemIdNumber.value
      };
      log('SOItemAttributesForm', 'handleSubmit', 'Updating item attribute', payload);
      await save(payload);
      eventBus.emit('globalMessage.success', t('Atributo de item atualizado com sucesso'));
    } else {
      const payload = {
        so_item_attribute_name: formData.so_item_attribute_name.trim(),
        so_item_attribute_type: formData.so_item_attribute_type
      };
      log('SOItemAttributesForm', 'handleSubmit', 'Creating item attribute', payload);
      await save(payload);
      eventBus.emit('globalMessage.success', t('Atributo de item criado com sucesso'));
    }

    emit('submit-success');
    resetForm();
  } catch (error) {
    logError('SOItemAttributesForm', 'handleSubmit', 'Error submitting form', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error',
      props.isEditMode
        ? t('Erro ao atualizar atributo de item') + ': ' + message
        : t('Erro ao criar atributo de item') + ': ' + message
    );
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.itemId, async (newId) => {
  log('SOItemAttributesForm', 'watch:itemId', 'itemId changed', { newId, isEditMode: props.isEditMode });
  if (newId && props.isEditMode) {
    await nextTick();
    await loadForEdit();
  }
});

watch(() => formData.so_item_attribute_type, async (newType, oldType) => {
  log('SOItemAttributesForm', 'watch:so_item_attribute_type', 'Type changed', { oldType, newType });
  if (newType !== 'select') {
    attributeOptions.value = [];
    resetOptionForm();
    return;
  }

  if (props.isEditMode && itemIdNumber.value) {
    await loadOptions();
  }
});

onMounted(() => {
  log('SOItemAttributesForm', 'mounted', 'Component mounted', {
    itemId: props.itemId,
    itemIdNumber: itemIdNumber.value,
    isEditMode: props.isEditMode
  });

  if (props.isEditMode && itemIdNumber.value) {
    loadForEdit();
  }
});

defineExpose({
  loadForEdit,
  handleSubmit,
  resetForm,
  validateForm,
  getItemName
});
</script>

<style scoped>
.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.error-message {
  color: var(--p-red-500);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}

.options-section {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--p-content-border-color, #e5e7eb);
}

.section-title {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
}

.empty-state {
  margin-bottom: 1rem;
  color: #6c757d;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.option-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem;
  border: 1px solid var(--p-content-border-color, #e5e7eb);
  border-radius: 6px;
}

.option-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.option-main {
  display: flex;
  align-items: center;
  gap: 8px;
}

.option-icon {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.option-icon :deep(svg) {
  width: 100%;
  height: 100%;
  stroke: none;
}

.option-value {
  font-weight: 500;
}

.svg-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 12px;
  background: var(--p-surface-50);
  border-radius: 6px;
}

.svg-preview-label {
  font-size: 0.85rem;
  color: var(--p-text-muted-color);
}

.svg-preview-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.svg-preview-icon :deep(svg) {
  width: 100%;
  height: 100%;
  stroke: none;
}

.option-order {
  color: #6c757d;
  font-size: 0.875rem;
}

.option-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.options-form {
  margin-top: 1rem;
}

.options-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.flex-container {
  display: flex;
  gap: 1rem;
}

.flex-item-half {
  flex: 1;
}

@media (max-width: 768px) {
  .flex-container {
    flex-direction: column;
    gap: 0;
  }

  .option-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .options-actions {
    justify-content: stretch;
  }
}
</style>
